import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Grid,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Modal,
    TextField,
    CircularProgress,
    IconButton,
    Divider
} from '@mui/material';

import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

import { Delete, AttachMoney, Edit, Cancel } from '@mui/icons-material';
import { deepPurple, red, blue } from '@mui/material/colors';

import { useSelector } from 'react-redux';
import { selectCurrentToken } from 'features/auth/authSlice';
import { axiosInstance } from 'utils/axios';
import { toast } from 'react-toastify';

const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    maxWidth: 900,
    width: '90%',
    maxHeight: '90vh',
    overflowY: 'auto',
    bgcolor: 'background.paper',
    boxShadow: 24,
    padding: '20px',
    borderRadius: '10px'
};

const smallModalStyle = {
    ...style,
    maxWidth: 400,
    minHeight: 'auto'
};

const AIPackages = () => {
    const token = useSelector(selectCurrentToken);

    // Filter States
    const [dateFrom, setDateFrom] = useState(dayjs().subtract(1, 'month'));
    const [dateTo, setDateTo] = useState(dayjs());
    const [customerLoginUserName, setCustomerLoginUserName] = useState('');
    const [customerNumberCode, setCustomerNumberCode] = useState('');

    // Table States
    const [salesList, setSalesList] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // Alerts
    const warningAlert = (msg) => toast.warning(msg, { position: toast.POSITION.TOP_CENTER });
    const successAlert = (msg) => toast.success(msg, { position: toast.POSITION.TOP_CENTER });
    const errorAlert = (msg) => toast.error(msg, { position: toast.POSITION.TOP_CENTER });

    // Fetch Sales List
    const fetchSales = async () => {
        if (!dateFrom.isValid() || !dateTo.isValid()) {
            warningAlert('Please select valid From and To dates.');
            return;
        }

        setIsLoading(true);
        let url = `/Backend/Customer/Package/Sales/All?dateFrom=${dateFrom.format('YYYY-MM-DD')}&dateTo=${dateTo.format('YYYY-MM-DD')}`;
        
        if (customerLoginUserName.trim()) url += `&customerLoginUserName=${customerLoginUserName}`;
        if (customerNumberCode.trim()) url += `&customerNumberCode=${customerNumberCode}`;

        try {
            const res = await axiosInstance.get(url, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.status === 200) {
                setSalesList(res.data);
            }
        } catch (error) {
            errorAlert('Failed to fetch sales data.');
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchSales();
    }, []);

    // --- DELETE SALES MODAL ---
    const DeleteSaleModal = ({ sale }) => {
        const [open, setOpen] = useState(false);
        const [isDeleting, setIsDeleting] = useState(false);

        const handleDelete = async () => {
            setIsDeleting(true);
            try {
                const res = await axiosInstance.post(`/Backend/Customer/Package/Sales/Delete/${sale.id}`, {}, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (res.status === 200) {
                    successAlert('Sale deleted successfully.');
                    setOpen(false);
                    fetchSales();
                }
            } catch (error) {
                errorAlert('Failed to delete sale.');
            } finally {
                setIsDeleting(false);
            }
        };

        return (
            <>
                <IconButton color="error" onClick={() => setOpen(true)} title="Delete Sale">
                    <Delete />
                </IconButton>
                <Modal open={open} onClose={() => setOpen(false)}>
                    <Box sx={smallModalStyle}>
                        <Typography variant="h5" sx={{ mb: 2 }}>Confirm Delete</Typography>
                        <Typography sx={{ mb: 3 }}>
                            Are you sure you want to delete this sale package <b>{sale.numberCode}</b>?
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                            <Button variant="outlined" onClick={() => setOpen(false)} disabled={isDeleting}>Cancel</Button>
                            <Button variant="contained" color="error" onClick={handleDelete} disabled={isDeleting}>
                                {isDeleting ? <CircularProgress size={20} color="inherit"/> : 'Delete'}
                            </Button>
                        </Box>
                    </Box>
                </Modal>
            </>
        );
    };

    // --- PAYOUT MODAL ---
    const PayoutModal = ({ sale }) => {
        const [open, setOpen] = useState(false);
        const [payouts, setPayouts] = useState([]);
        const [isFetchingPayouts, setIsFetchingPayouts] = useState(false);
        const [isSubmitting, setIsSubmitting] = useState(false);

        // Form States
        const [editingId, setEditingId] = useState(0);
        const [amount, setAmount] = useState('');
        const [indexName, setIndexName] = useState('');
        const [indexCode, setIndexCode] = useState('');
        const [buyPrice, setBuyPrice] = useState('');
        const [buyQuantity, setBuyQuantity] = useState('');
        const [buyFee, setBuyFee] = useState('');
        const [buyTime, setBuyTime] = useState('');
        const [sellPrice, setSellPrice] = useState('');
        const [sellQuantity, setSellQuantity] = useState('');
        const [sellFee, setSellFee] = useState('');
        const [sellTime, setSellTime] = useState('');
        const [priceChange, setPriceChange] = useState('');
        const [finalPNL, setFinalPNL] = useState('');

        const clearForm = () => {
            setEditingId(0);
            setAmount(''); setIndexName(''); setIndexCode('');
            setBuyPrice(''); setBuyQuantity(''); setBuyFee(''); setBuyTime('');
            setSellPrice(''); setSellQuantity(''); setSellFee(''); setSellTime('');
            setPriceChange(''); setFinalPNL('');
        };

        // --- ADD THESE NEW FUNCTIONS ---
        const [isSearchingIndex, setIsSearchingIndex] = useState(false);

        // Step 1: Search Stock API
        const handleSearchIndex = async () => {
            if (!indexCode) return;
            setIsSearchingIndex(true);
            try {
                const res = await axiosInstance.get(`/USStock/Search?usStockCode=${indexCode}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                
                if (res.status === 200 && res.data) {
                    // Sanitize the name to remove commas, periods, and special characters
                    // e.g., "Tesla, Inc." becomes "Tesla Inc"
                    const safeName = res.data.name 
                        ? res.data.name.replace(/[^a-zA-Z0-9\s]/g, '').trim() 
                        : '';
                        
                    setIndexName(safeName);
                    setSellPrice(res.data.price || '');
                    successAlert('Stock found! Name and Sell Price updated.');
                } else {
                    warningAlert('Stock not found.');
                }
            } catch (error) {
                errorAlert('Failed to search stock. Check symbol.');
            } finally {
                setIsSearchingIndex(false);
            }
        };

        // Step 4: Auto-Calculate Math with realistic organic values
        const handleAutoCalculate = () => {
            const amt = parseFloat(amount);
            const pnl = parseFloat(finalPNL);
            const sPrice = parseFloat(sellPrice);

            if (isNaN(amt) || isNaN(pnl) || isNaN(sPrice) || sPrice === 0) {
                warningAlert('Ensure Amount, Final PNL, and Sell Price have valid numbers first.');
                return;
            }

            // --- 1. Math Logic ---
            const sellValue = amt + pnl;
            const qty = sellValue / sPrice;
            const bPrice = amt / qty;
            const pChange = sPrice - bPrice;

            // --- 2. Realistic Fees (Approx 0.1% trading fee + random cents noise) ---
            const calculatedBuyFee = (amt * 0.001) + (Math.random() * 0.5);
            const calculatedSellFee = (sellValue * 0.001) + (Math.random() * 0.5);

            // --- 3. Realistic Times (Constrained by sale.createdDate & NO WEEKENDS) ---
            const now = new Date();
            const createdDate = new Date(sale.createdDate || now); 

            // Helper function to push dates off the weekend (Sat=6, Sun=0)
            const avoidWeekend = (timestamp, minLimitMs) => {
                let d = new Date(timestamp);
                if (d.getDay() === 0 || d.getDay() === 6) {
                    // Try pushing backward to Friday first
                    d.setDate(d.getDate() - (d.getDay() === 6 ? 1 : 2));
                    
                    // If pushing to Friday goes BEFORE the package was created, 
                    // push forward to Monday instead.
                    if (d.getTime() < minLimitMs) {
                        d = new Date(timestamp);
                        d.setDate(d.getDate() + (d.getDay() === 6 ? 2 : 1));
                    }
                    
                    // If pushing to Monday goes into the future, force back to Friday
                    if (d.getTime() > now.getTime()) {
                        d = new Date(timestamp);
                        d.setDate(d.getDate() - (d.getDay() === 6 ? 1 : 2));
                    }
                }
                return d.getTime();
            };
            
            // Sell Time: Randomize between 2 to 15 minutes ago
            const randomSellMinutes = Math.floor(Math.random() * 14) + 2; 
            let sellTimeMs = now.getTime() - (randomSellMinutes * 60000);
            
            // Safety Check: Ensure Sell Time is strictly AFTER the Created Date
            if (sellTimeMs <= createdDate.getTime()) {
                sellTimeMs = createdDate.getTime() + (5 * 60000); 
            }
            
            // Apply Weekend check to Sell Time
            sellTimeMs = avoidWeekend(sellTimeMs, createdDate.getTime());
            const sellDate = new Date(sellTimeMs);
            
            // Buy Time: Try to set it 1 to 5 days before Sell Time
            const randomDays = Math.floor(Math.random() * 5) + 1;
            const randomHours = Math.floor(Math.random() * 24);
            const randomMinutes = Math.floor(Math.random() * 60);
            const timeOffset = (randomDays * 24 * 60 * 60 * 1000) + (randomHours * 60 * 60 * 1000) + (randomMinutes * 60000);
            
            let buyTimeMs = sellDate.getTime() - timeOffset;

            // Safety Check: If offset pushes BEFORE Created Date, pick random time in window
            if (buyTimeMs <= createdDate.getTime()) {
                const timeWindow = sellDate.getTime() - createdDate.getTime();
                buyTimeMs = createdDate.getTime() + (timeWindow * (Math.random() * 0.7 + 0.1));
            }
            
            // Apply Weekend check to Buy Time
            buyTimeMs = avoidWeekend(buyTimeMs, createdDate.getTime());
            const buyDate = new Date(buyTimeMs);

            const formatForInput = (d) => {
                const pad = (n) => n.toString().padStart(2, '0');
                return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
            };

            // --- 4. Auto-fill fields ---
            setBuyQuantity(qty.toFixed(4));
            setSellQuantity(qty.toFixed(4));
            setBuyPrice(bPrice.toFixed(4));
            setPriceChange(pChange.toFixed(4));
            
            setBuyFee(calculatedBuyFee.toFixed(2));
            setSellFee(calculatedSellFee.toFixed(2));
            
            setBuyTime(formatForInput(buyDate));
            setSellTime(formatForInput(sellDate));

            successAlert('Trade details auto-calculated with realistic values!');
        };
        // -------------------------------

        const fetchPayouts = async () => {
            setIsFetchingPayouts(true);
            try {
                const res = await axiosInstance.get(`/Backend/Customer/Package/Payout?salesID=${sale.id}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (res.status === 200) setPayouts(res.data);
            } catch (error) {
                errorAlert('Failed to load payouts.');
            } finally {
                setIsFetchingPayouts(false);
            }
        };

        const handleOpen = () => {
            setOpen(true);
            fetchPayouts();
            clearForm();
        };

        const handleEdit = (p) => {
            setEditingId(p.id);
            setAmount(p.amount);
            setIndexName(p.indexName || '');
            setIndexCode(p.indexCode || '');
            setBuyPrice(p.buyPrice);
            setBuyQuantity(p.buyQuantity);
            setBuyFee(p.buyFee);
            setBuyTime(p.buyTime ? p.buyTime.substring(0, 16) : '');
            setSellPrice(p.sellPrice);
            setSellQuantity(p.sellQuantity);
            setSellFee(p.sellFee);
            setSellTime(p.sellTime ? p.sellTime.substring(0, 16) : '');
            setPriceChange(p.priceChange);
            setFinalPNL(p.finalPNL);
        };

        const handleDeletePayout = async (id) => {
            if (!window.confirm("Are you sure you want to delete this payout?")) return;
            try {
                const res = await axiosInstance.post(`/Backend/Customer/Package/Payout/Delete/${id}`, {}, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (res.status === 200) {
                    successAlert('Payout deleted.');
                    fetchPayouts();
                }
            } catch (error) {
                errorAlert('Failed to delete payout.');
            }
        };

        const handleSubmit = async () => {
            // --- ADD THIS WEEKEND VALIDATION BLOCK ---
            if (buyTime) {
                const buyDay = new Date(buyTime).getDay(); // 0 is Sunday, 6 is Saturday
                if (buyDay === 0 || buyDay === 6) {
                    warningAlert('Buy Time cannot be on a weekend (Saturday or Sunday).');
                    return; // Stop submission
                }
            }

            if (sellTime) {
                const sellDay = new Date(sellTime).getDay();
                if (sellDay === 0 || sellDay === 6) {
                    warningAlert('Sell Time cannot be on a weekend (Saturday or Sunday).');
                    return; // Stop submission
                }
            }
            // -----------------------------------------

            setIsSubmitting(true);
            const toLocalISOString = (dateString) => {
                if (!dateString) return null;
                const d = new Date(dateString);
                return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString();
            };
            
            // ... (rest of your existing handleSubmit code stays exactly the same)
            const payload = {
                amount: parseFloat(amount || 0),
                indexName,
                indexCode,
                buyPrice: parseFloat(buyPrice || 0),
                buyQuantity: parseFloat(buyQuantity || 0),
                buyFee: parseFloat(buyFee || 0),
                buyTime: toLocalISOString(buyTime),
                sellPrice: parseFloat(sellPrice || 0),
                sellQuantity: parseFloat(sellQuantity || 0),
                sellFee: parseFloat(sellFee || 0),
                sellTime: toLocalISOString(sellTime),
                priceChange: parseFloat(priceChange || 0),
                finalPNL: parseFloat(finalPNL || 0)
            };

            try {
                let res;
                if (editingId > 0) {
                    payload.id = editingId;
                    res = await axiosInstance.put('/Backend/Customer/Package/Payout', payload, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                } else {
                    payload.customerID = sale.customerID;
                    payload.salesID = sale.id;
                    res = await axiosInstance.post('/Backend/Customer/Package/Payout', payload, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                }

                if (res.status === 200 || res.status === 201) {
                    successAlert(editingId > 0 ? 'Payout updated.' : 'Payout added.');
                    clearForm();
                    fetchPayouts();
                }
            } catch (error) {
                errorAlert('Failed to save payout.');
            } finally {
                setIsSubmitting(false);
            }
        };

        return (
            <>
                <IconButton color="primary" onClick={handleOpen} title="Manage Payouts">
                    <AttachMoney />
                </IconButton>
                <Modal open={open} onClose={() => setOpen(false)}>
                    <Box sx={style}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                            <Typography variant="h4">Payouts - {sale.numberCode}</Typography>
                            <Cancel onClick={() => setOpen(false)} sx={{ cursor: 'pointer' }} />
                        </Box>

                        {/* Payouts Table */}
                        <TableContainer component={Paper} sx={{ maxHeight: 300, mb: 3 }}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell><b>Index Info</b></TableCell>
                                        <TableCell><b>Trade Summary</b></TableCell>
                                        <TableCell><b>Buy Details</b></TableCell>
                                        <TableCell><b>Sell Details</b></TableCell>
                                        <TableCell align="center"><b>Actions</b></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {isFetchingPayouts ? (
                                        <TableRow><TableCell colSpan={5} align="center"><CircularProgress size={24}/></TableCell></TableRow>
                                    ) : payouts.length === 0 ? (
                                        <TableRow><TableCell colSpan={5} align="center">No payouts found.</TableCell></TableRow>
                                    ) : (
                                        payouts.map(p => (
                                            <TableRow key={p.id} hover>
                                                <TableCell sx={{ fontSize: '13px' }}>
                                                    <b>{p.indexName || '-'}</b><br/>
                                                    <span style={{ color: 'gray' }}>{p.indexCode || '-'}</span>
                                                </TableCell>
                                                <TableCell sx={{ fontSize: '13px' }}>
                                                    Amount: ${p.amount}<br/>
                                                    Change: {p.priceChange}<br/>
                                                    PNL: <b style={{ color: p.finalPNL >= 0 ? 'green' : 'red' }}>${p.finalPNL}</b>
                                                </TableCell>
                                                {/* --- UPDATE THESE TWO CELLS --- */}
                                                <TableCell sx={{ fontSize: '13px' }}>
                                                    Time: {p.buyTime ? dayjs(p.buyTime).format('YYYY-MM-DD hh:mm A') : '-'}<br/>
                                                    Price: ${p.buyPrice} | Qty: {p.buyQuantity}<br/>
                                                    Fee: ${p.buyFee}
                                                </TableCell>
                                                <TableCell sx={{ fontSize: '13px' }}>
                                                    Time: {p.sellTime ? dayjs(p.sellTime).format('YYYY-MM-DD hh:mm A') : '-'}<br/>
                                                    Price: ${p.sellPrice} | Qty: {p.sellQuantity}<br/>
                                                    Fee: ${p.sellFee}
                                                </TableCell>
                                                {/* ------------------------------ */}
                                                <TableCell align="center">
                                                    <IconButton color="primary" size="small" onClick={() => handleEdit(p)}><Edit fontSize="small"/></IconButton>
                                                    <IconButton color="error" size="small" onClick={() => handleDeletePayout(p.id)}><Delete fontSize="small"/></IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <Divider sx={{ mb: 2 }} />
                        <Typography variant="h5" sx={{ mb: 2, color: editingId > 0 ? blue[700] : 'inherit' }}>
                            {editingId > 0 ? 'Edit Payout' : 'Add New Payout'}
                        </Typography>

                        {/* Form */}
                        <Box sx={{ p: 2, bgcolor: '#f8f9fa', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                            
                            {/* General Info & Auto-Calculate */}
                            <Typography variant="subtitle2" sx={{ color: deepPurple[500], fontWeight: 'bold', mb: 1 }}>
                                Step 1 & 2: Search Asset & Set Targets
                            </Typography>
                            <Grid container spacing={2} sx={{ mb: 2 }} alignItems="center">
                                <Grid item xs={12} sm={3}>
                                    <TextField 
                                        fullWidth size="small" label="1. Index Code" 
                                        value={indexCode} onChange={(e) => setIndexCode(e.target.value)} 
                                    />
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <Button 
                                        fullWidth variant="contained" color="secondary" 
                                        onClick={handleSearchIndex} disabled={isSearchingIndex || !indexCode}
                                    >
                                        {isSearchingIndex ? <CircularProgress size={20} color="inherit" /> : "Search API"}
                                    </Button>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField fullWidth size="small" label="Index Name (Auto)" value={indexName} onChange={(e) => setIndexName(e.target.value)} />
                                </Grid>

                                <Grid item xs={12} sm={4}>
                                    <TextField fullWidth size="small" label="2. Amount" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField fullWidth size="small" label="3. Final PNL" type="number" value={finalPNL} onChange={(e) => setFinalPNL(e.target.value)} />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <Button fullWidth variant="contained" color="primary" onClick={handleAutoCalculate}>
                                        4. Auto-Fill Details
                                    </Button>
                                </Grid>
                            </Grid>
                            
                            <Divider sx={{ my: 2 }} />

                            {/* Buy Details */}
                            <Typography variant="subtitle2" sx={{ color: deepPurple[500], fontWeight: 'bold', mb: 1 }}>
                                Buy Details
                            </Typography>
                            <Grid container spacing={2} sx={{ mb: 2 }}>
                                <Grid item xs={12} sm={3}>
                                    <TextField fullWidth size="small" label="Buy Price" type="number" value={buyPrice} onChange={(e) => setBuyPrice(e.target.value)} />
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <TextField fullWidth size="small" label="Buy Quantity" type="number" value={buyQuantity} onChange={(e) => setBuyQuantity(e.target.value)} />
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <TextField fullWidth size="small" label="Buy Fee" type="number" value={buyFee} onChange={(e) => setBuyFee(e.target.value)} />
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <TextField fullWidth size="small" label="Buy Time" type="datetime-local" InputLabelProps={{ shrink: true }} value={buyTime} onChange={(e) => setBuyTime(e.target.value)} />
                                </Grid>
                            </Grid>

                            <Divider sx={{ my: 2 }} />

                            {/* Sell Details */}
                            <Typography variant="subtitle2" sx={{ color: deepPurple[500], fontWeight: 'bold', mb: 1 }}>
                                Sell Details
                            </Typography>
                            <Grid container spacing={2} sx={{ mb: 2 }}>
                                <Grid item xs={12} sm={3}>
                                    <TextField fullWidth size="small" label="Sell Price (Auto)" type="number" value={sellPrice} onChange={(e) => setSellPrice(e.target.value)} />
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <TextField fullWidth size="small" label="Sell Quantity" type="number" value={sellQuantity} onChange={(e) => setSellQuantity(e.target.value)} />
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <TextField fullWidth size="small" label="Sell Fee" type="number" value={sellFee} onChange={(e) => setSellFee(e.target.value)} />
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <TextField fullWidth size="small" label="Sell Time" type="datetime-local" InputLabelProps={{ shrink: true }} value={sellTime} onChange={(e) => setSellTime(e.target.value)} />
                                </Grid>
                            </Grid>

                            <Divider sx={{ my: 2 }} />

                            {/* Results */}
                            <Typography variant="subtitle2" sx={{ color: deepPurple[500], fontWeight: 'bold', mb: 1 }}>
                                Results
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField fullWidth size="small" label="Price Change" type="number" value={priceChange} onChange={(e) => setPriceChange(e.target.value)} />
                                </Grid>
                            </Grid>
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                            {editingId > 0 && <Button variant="outlined" onClick={clearForm} disabled={isSubmitting}>Cancel Edit</Button>}
                            <Button variant="contained" sx={{ bgcolor: deepPurple[500] }} onClick={handleSubmit} disabled={isSubmitting}>
                                {isSubmitting ? <CircularProgress size={24} color="inherit" /> : (editingId > 0 ? 'Update Payout' : 'Add Payout')}
                            </Button>
                        </Box>
                    </Box>
                </Modal>
            </>
        );
    };

    return (
        <Grid container spacing={3}>
            {/* FILTERS */}
            <Grid item xs={12}>
                <Paper sx={{ p: 3 }}>
                    <Typography variant="h4" sx={{ mb: 2 }}>AI Packages filter</Typography>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={6} md={3}>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    label="Date From *"
                                    value={dateFrom}
                                    onChange={(val) => setDateFrom(val)}
                                    slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                                />
                            </LocalizationProvider>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    label="Date To *"
                                    value={dateTo}
                                    onChange={(val) => setDateTo(val)}
                                    slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                                />
                            </LocalizationProvider>
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Username"
                                value={customerLoginUserName}
                                onChange={(e) => setCustomerLoginUserName(e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Customer Code"
                                value={customerNumberCode}
                                onChange={(e) => setCustomerNumberCode(e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <Button fullWidth variant="contained" sx={{ bgcolor: deepPurple[500] }} onClick={fetchSales} disabled={isLoading}>
                                {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Filter'}
                            </Button>
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>

            {/* TABLE */}
            <Grid item xs={12}>
                <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                    <TableContainer sx={{ maxHeight: 600 }}>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell><b>Sale Code</b></TableCell>
                                    <TableCell><b>Customer</b></TableCell>
                                    <TableCell><b>Code</b></TableCell>
                                    <TableCell><b>Package Name</b></TableCell>
                                    <TableCell><b>Amount</b></TableCell>
                                    
                                    {/* --- ADD THIS NEW HEADER --- */}
                                    <TableCell><b>Created Date</b></TableCell>
                                    
                                    <TableCell><b>Matured Date</b></TableCell>
                                    <TableCell align="center"><b>Actions</b></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {salesList.length === 0 && !isLoading && (
                                    <TableRow>
                                        {/* --- UPDATE colSpan to 8 --- */}
                                        <TableCell colSpan={8} align="center">No AI Packages found.</TableCell>
                                    </TableRow>
                                )}
                                {salesList.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => (
                                    <TableRow hover key={row.id}>
                                        <TableCell>{row.numberCode}</TableCell>
                                        <TableCell>{row.customerLoginUserName}</TableCell>
                                        <TableCell>{row.customerNumberCode}</TableCell>
                                        <TableCell>{row.packageName}</TableCell>
                                        <TableCell>${row.amount}</TableCell>
                                        
                                        {/* --- UPDATE THESE TWO CELLS --- */}
                                        <TableCell>
                                            {row.createdDate ? dayjs(row.createdDate).format('YYYY-MM-DD hh:mm A') : '-'}
                                        </TableCell>

                                        <TableCell>
                                            {row.maturedDate ? dayjs(row.maturedDate).format('YYYY-MM-DD hh:mm A') : '-'}
                                        </TableCell>
                                        {/* ------------------------------ */}

                                        <TableCell align="center">
                                            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                                                <PayoutModal sale={row} />
                                                <DeleteSaleModal sale={row} />
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        rowsPerPageOptions={[10, 25, 100]}
                        component="div"
                        count={salesList.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={(e, newPage) => setPage(newPage)}
                        onRowsPerPageChange={(e) => { setRowsPerPage(+e.target.value); setPage(0); }}
                    />
                </Paper>
            </Grid>
        </Grid>
    );
};

export default AIPackages;